import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Checkbox, Collapse, Stack, Typography } from '@mui/material';
import { IDomain } from '@store/services/projects/types';
import { themePalette } from '@theme/muiTheme';
import { findWideDomain } from '@utils/domains';
import { DropdownIcon } from '@utils/iconsDefs';
import {
  buildTree,
  getScopeRowIndent,
  getTreeCheckState,
  GetTreeCheckStateOptions,
  ITreeItem,
  TreeCheckState,
} from '@views/Project/helpers';

const CHECK_STATE_OPTIONS: GetTreeCheckStateOptions = {
  isDerivedFromSubtree: true,
};

const subtreeIds = (node: ITreeItem): string[] => [
  node.id,
  ...(node.children?.flatMap(subtreeIds) ?? []),
];

const collectParentIds = (nodes: ITreeItem[]): string[] =>
  nodes.flatMap((node) =>
    node.children?.length ? [node.id, ...collectParentIds(node.children)] : [],
  );

type RowProps = {
  node: ITreeItem;
  depth: number;
  selectedSet: Set<string>;
  expanded: Set<string>;
  onToggleSelect: (node: ITreeItem) => void;
  onToggleExpand: (id: string) => void;
  isWide?: boolean;
  checkState?: TreeCheckState;
};

const TreeRow: FC<RowProps> = ({
  node,
  depth,
  selectedSet,
  expanded,
  onToggleSelect,
  onToggleExpand,
  isWide = false,
  checkState,
}) => {
  const hasChildren = !!node.children?.length;
  const state =
    checkState ?? getTreeCheckState(node, selectedSet, CHECK_STATE_OPTIONS);
  const isOpen = expanded.has(node.id);

  return (
    <>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          cursor: hasChildren ? 'pointer' : 'default',
          '&:hover': { backgroundColor: themePalette.grey[400] },
          pl: `${getScopeRowIndent(depth)}px`,
        }}
        pr="24px"
        py="16px"
        onClick={() => hasChildren && onToggleExpand(node.id)}
      >
        <Stack direction="row" alignItems="center" gap={2}>
          <Checkbox
            checked={state === 'checked'}
            indeterminate={state === 'indeterminate'}
            onClick={(event) => {
              event.stopPropagation();
              onToggleSelect(node);
            }}
          />
          <Typography variant={isWide ? 'h5' : 'body1'}>{node.name}</Typography>
        </Stack>

        {hasChildren && (
          <DropdownIcon
            fill={themePalette.grey[1000]}
            style={{ transform: isOpen ? 'rotate(180deg)' : '', minWidth: 20 }}
          />
        )}
      </Stack>

      {hasChildren && (
        <Collapse in={isOpen} unmountOnExit>
          {node.children?.map((child) => (
            <TreeRow
              key={child.id}
              node={child}
              depth={depth + 1}
              selectedSet={selectedSet}
              expanded={expanded}
              onToggleSelect={onToggleSelect}
              onToggleExpand={onToggleExpand}
            />
          ))}
        </Collapse>
      )}
    </>
  );
};

type Props = {
  domains: IDomain[];
  selected: string[];
  onChange: (ids: string[]) => void;
};

export const AddScopeTree: FC<Props> = ({ domains, selected, onChange }) => {
  const { wideLeaf, wideNode, childRows } = useMemo(() => {
    const roots = buildTree(domains);
    const wideId = findWideDomain(domains)?.id;
    const wideRoot = roots.find((root) => root.id === wideId);

    if (!wideRoot) {
      return {
        wideLeaf: null as ITreeItem | null,
        wideNode: null as ITreeItem | null,
        childRows: roots,
      };
    }

    return {
      wideLeaf: { id: wideRoot.id, name: wideRoot.name, children: [] },
      wideNode: wideRoot,
      childRows: [
        ...roots.filter((root) => root.id !== wideId),
        ...(wideRoot.children ?? []),
      ],
    };
  }, [domains]);

  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const didInitExpand = useRef(false);
  const selectedSet = useMemo(() => new Set(selected), [selected]);

  useEffect(() => {
    if (didInitExpand.current || childRows.length === 0) return;
    setExpanded(new Set(collectParentIds(childRows)));
    didInitExpand.current = true;
  }, [childRows]);

  const toggleExpand = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });

  const wideCheckState = useMemo<TreeCheckState | undefined>(() => {
    if (!wideNode) return undefined;
    if (selectedSet.has(wideNode.id)) return 'checked';

    const hasSelectedDomain = (wideNode.children ?? []).some((child) =>
      subtreeIds(child).some((id) => selectedSet.has(id)),
    );

    return hasSelectedDomain ? 'indeterminate' : 'unchecked';
  }, [wideNode, selectedSet]);

  const toggleSelect = (node: ITreeItem) => {
    const ids = subtreeIds(node);
    const next = new Set(selectedSet);
    if (
      getTreeCheckState(node, selectedSet, CHECK_STATE_OPTIONS) === 'checked'
    ) {
      ids.forEach((id) => next.delete(id));
    } else {
      ids.forEach((id) => next.add(id));
    }
    onChange(Array.from(next));
  };

  return (
    <Box>
      {wideLeaf && (
        <TreeRow
          node={wideLeaf}
          depth={0}
          selectedSet={selectedSet}
          expanded={expanded}
          onToggleSelect={toggleSelect}
          onToggleExpand={toggleExpand}
          checkState={wideCheckState}
          isWide
        />
      )}

      {childRows.map((row) => (
        <TreeRow
          key={row.id}
          node={row}
          depth={wideLeaf ? 1 : 0}
          selectedSet={selectedSet}
          expanded={expanded}
          onToggleSelect={toggleSelect}
          onToggleExpand={toggleExpand}
        />
      ))}
    </Box>
  );
};
